/**
 * Sentry frontend initialization for Inbest Hub.
 *
 * Card kqb7X5iN — OBS F0.3 Sentry Performance + Session Replay frontend.
 *
 * Graceful: if VITE_SENTRY_DSN is not set in build-time env, init is skipped
 * and a console.warn is emitted. Local dev / CI build without Sentry creds
 * keeps working; prod gets full tracking once VITE_SENTRY_DSN is configured
 * in Vercel env vars.
 *
 * Testability: exposes a pure builder `buildSentryConfig()` that returns the
 * exact object that would be passed to `Sentry.init(...)`. This lets unit
 * tests assert defaults / env overrides / privacy invariants without
 * touching Sentry runtime.
 *
 * Card mggUrIZI — qué es «esencial» y qué pide consentimiento:
 *
 * - **Esencial (siempre, también con «Solo esenciales» y antes del banner):**
 *   el SDK se inicializa y reporta ERRORES técnicos — excepciones no
 *   capturadas, rechazos de promesas, el corte del stream del chat
 *   (`reportChatStreamError`) y los `captureMessage` técnicos. Sin PII
 *   (`sendDefaultPii: false` + `sentryScrub`), sin Replay, sin trazas de
 *   performance y sin breadcrumbs de interacción (`ui.*`). El SDK no escribe
 *   cookies ni storage en este modo.
 * - **Con consentimiento de analíticas:** Performance (`browserTracing`, que
 *   mide clicks e INP y nombra el elemento), Session Replay (graba el DOM y
 *   guarda su sesión en `sessionStorage`), breadcrumbs de interacción y los
 *   eventos de producto (`trackEvent`, que ya tenía su propio gate).
 *
 * El consentimiento se re-lee en cada decisión (sampler, breadcrumb,
 * transacción) y se escucha `consent-changed`: aceptar en el banner prende lo
 * opcional sin recargar, y revocar en el modal lo apaga (Replay se detiene; las
 * trazas nuevas dejan de muestrearse). Volver a aceptar tras revocar no
 * reinicia Replay hasta recargar la página.
 */
import * as Sentry from '@sentry/react';

import { resolveEnvironment, resolveRelease } from './analytics/buildEnv';
import { CONSENT_CHANGED_EVENT, hasAnalyticsConsent } from './analytics/consent';
import { hasSensitiveUrlParams, sanitizeConsoleBreadcrumb, scrubDeep } from './sentryScrub';

let initialized = false;
/** Poll que espera una URL sin params sensibles para arrancar Replay. */
let deferredReplayTimer: ReturnType<typeof setInterval> | null = null;
/** Card mggUrIZI — lo opcional ya se pidió una vez en esta carga de página. */
let tracingAdded = false;
let replayRequested = false;
/** Listener de `consent-changed` registrado por `initSentry`. */
let consentListener: (() => void) | null = null;

/** Cada cuánto se re-chequea la URL mientras Replay está diferido. */
const REPLAY_URL_POLL_MS = 500;

/**
 * Atributos del DOM que Replay graba enmascarados (`***`). Card CW31sjZO
 * (iteración #3).
 *
 * `maskAllText` enmascara los nodos de texto, NO los atributos: el SDK sólo
 * enmascara los de esta lista (su default es `title` y `placeholder`). El QA
 * encontró el `session_id` en `data-testid="message-intro-{id}"` dentro del
 * DOM grabado, y no es el único lugar: `SidebarHistory` pone el id de cada
 * sesión del usuario en `data-testid`, y su `aria-label` lleva el título de la
 * conversación. Enmascarar el atributo cubre cualquier componente, presente o
 * futuro, sin depender de que alguien se acuerde de no meter un id ahí.
 *
 * `href`/`src` no pasan por acá (rrweb los resuelve antes); hoy ningún link
 * del chat lleva el id.
 */
export const REPLAY_MASKED_ATTRIBUTES = ['title', 'placeholder', 'aria-label', 'data-testid', 'id'];

/** `@sentry/react` no re-exporta el tipo; se toma de la firma del hook. */
type SentryTransactionEvent = Parameters<NonNullable<Sentry.BrowserOptions['beforeSendTransaction']>>[0];

/**
 * Config shape returned to `Sentry.init(...)`. Kept partial-explicit so the
 * unit tests can assert individual fields without pinning the whole shape.
 */
export interface SentryConfig {
  dsn: string;
  environment: string;
  release: string | undefined;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  sendDefaultPii: boolean;
  /**
   * Integraciones de `Sentry.init`: las ESENCIALES (hoy, ninguna además de
   * las default del SDK). Card mggUrIZI: `browserTracing` ya no va acá — ver
   * `tracing`.
   */
  integrations: unknown[];
  /**
   * Card mggUrIZI — Performance. `initSentry` la agrega con
   * `Sentry.addIntegration` sólo cuando hay consentimiento de analíticas.
   */
  tracing: ReturnType<typeof Sentry.browserTracingIntegration>;
  /**
   * Card mggUrIZI — muestreo de trazas atado al consentimiento: `0` sin
   * consentimiento (también después de revocarlo), `tracesSampleRate` con él.
   */
  tracesSampler: NonNullable<Sentry.BrowserOptions['tracesSampler']>;
  /**
   * Mirror of the replay integration options used to build `integrations`.
   * Not consumed by Sentry (`integrations` is what runs) — but exposed so
   * tests can verify the privacy invariant (`maskAllText: true`) without
   * introspecting integration internals.
   */
  replayOptions: {
    maskAllText: boolean;
    blockAllMedia: boolean;
    maskAttributes: string[];
  };
  /**
   * Card CW31sjZO (iteración #3) — Replay NO va en `integrations`:
   * `initSentry` la agrega con `Sentry.addIntegration` recién cuando la URL no
   * lleva params sensibles (ver `hasSensitiveUrlParams`).
   */
  replay: ReturnType<typeof Sentry.replayIntegration>;
  /**
   * Card CW31sjZO (iter #2) — scrubbing de ids sensibles (`session_id` del
   * chat, tokens en query, UUIDs) en todo lo que sale del browser. Ver
   * `sentryScrub.ts`. Breadcrumbs (console / xhr / fetch / navigation), el
   * evento de error, las transacciones (URLs de los spans `http.client`) y los
   * eventos custom de Replay.
   *
   * Card mggUrIZI: además, sin consentimiento `beforeBreadcrumb` descarta los
   * breadcrumbs de interacción (`ui.click`, `ui.input`) y, siempre, deja de
   * los de consola sólo el primer argumento string
   * (`sanitizeConsoleBreadcrumb`); `beforeSendTransaction` descarta la
   * transacción si el consentimiento se revocó mientras corría.
   */
  beforeBreadcrumb: (breadcrumb: Sentry.Breadcrumb) => Sentry.Breadcrumb | null;
  beforeSend: (event: Sentry.ErrorEvent) => Sentry.ErrorEvent | null;
  beforeSendTransaction: (event: SentryTransactionEvent) => SentryTransactionEvent | null;
  /**
   * Spans que viajan SUELTOS (item `span` del envelope: INP / interacciones).
   * Su `description` es el selector del elemento clickeado
   * (`button#entry-{id}[aria-label="…"]`), y no pasan por
   * `beforeSendTransaction`. Medido en el envelope real (iteración #3).
   */
  beforeSendSpan: (span: SentrySpanJSON) => SentrySpanJSON;
}

/** `@sentry/react` no re-exporta el tipo; se toma de la firma del hook. */
type SentrySpanJSON = Parameters<NonNullable<Sentry.BrowserOptions['beforeSendSpan']>>[0];

/**
 * Hooks de scrubbing. Breadcrumbs se limpian al AGREGARSE (así ni siquiera
 * quedan en el buffer del scope); `beforeSend` vuelve a pasar por el evento
 * entero porque `request.url`, `exception.values[].value` y los breadcrumbs
 * que se hayan agregado por otro camino también viajan.
 */
const scrubBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null => {
  // Card mggUrIZI — el rastro de clicks / inputs es analítica de uso.
  if (breadcrumb.category?.startsWith('ui.') && !hasAnalyticsConsent()) return null;
  return scrubDeep(sanitizeConsoleBreadcrumb(breadcrumb));
};
/**
 * Card mggUrIZI (iteración #2) — la IP y la ubicación del usuario no se
 * guardan en Sentry, sin depender de ningún setting del proyecto.
 *
 * El SDK 8 no manda la IP (`sendDefaultPii: false`), pero el servidor de
 * Sentry (Relay) la completa: para `platform: "javascript"`, si el evento NO
 * trae `sdk.settings.infer_ip`, aplica la regla «legacy» y un
 * `user.ip_address` vacío vale como `{{auto}}` → guarda la IP de la conexión.
 * Aparte, Relay rellena `user.geo` (país / ciudad / región) con un lookup
 * GeoIP de la IP de la conexión aunque el evento no tenga IP, salvo que
 * `user.geo` ya venga presente. (Fuente: `getsentry/relay`,
 * `relay-event-normalization/src/event.rs`, `normalize_ip_addresses` y
 * `normalize_user_geoinfo`; `infer_ip` existe desde Relay 25.3.0.)
 *
 * Por eso, en cada evento que sale (error, transacción, replay):
 * - `sdk.settings.infer_ip = "never"` — lo mismo que manda el SDK actual de
 *   Sentry cuando no se opta por recolectar la IP;
 * - se borra cualquier `user.ip_address` y se manda `user.geo = {}`, que
 *   Relay toma como «ya poblado» y no completa (el objeto vacío no se guarda).
 */
type IpSafeEvent = { sdk?: Sentry.Event['sdk']; user?: Sentry.Event['user'] };

export function preventIpAndGeoInference<T extends IpSafeEvent>(event: T): T {
  const sdk = (event.sdk ?? {}) as NonNullable<Sentry.Event['sdk']> & {
    settings?: Record<string, unknown>;
  };
  event.sdk = { ...sdk, settings: { ...(sdk.settings ?? {}), infer_ip: 'never' } } as T['sdk'];
  const user = { ...(event.user ?? {}) } as Record<string, unknown>;
  delete user.ip_address;
  user.geo = {};
  event.user = user as T['user'];
  return event;
}

const scrubErrorEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent =>
  preventIpAndGeoInference(scrubDeep(event));
/**
 * Card mggUrIZI — una transacción que arrancó con consentimiento y termina
 * después de revocarlo no sale.
 */
const scrubTransactionEvent = (event: SentryTransactionEvent): SentryTransactionEvent | null =>
  hasAnalyticsConsent() ? preventIpAndGeoInference(scrubDeep(event)) : null;
const scrubSpan = (span: SentrySpanJSON): SentrySpanJSON => scrubDeep(span);

/**
 * Event processor para el `replay_event` (el que acompaña a cada segmento de
 * Replay). No pasa por `beforeSend`, y lleva `urls` (las URLs visitadas) y
 * `request.url`. Los otros tipos ya tienen su hook.
 */
const scrubReplayEvent = (event: Sentry.Event): Sentry.Event =>
  (event.type as string | undefined) === 'replay_event'
    ? preventIpAndGeoInference(scrubDeep(event))
    : event;

/**
 * Read env with fallback to a string default, then parse as float.
 * `parseFloat` mirrors the contract in card kqb7X5iN (Criterio 1). Never
 * throws — invalid input falls back to the default.
 */
function readSampleRate(envKey: string, fallback: string): number {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = (import.meta.env as any)[envKey] ?? fallback;
  const parsed = parseFloat(String(raw));
  if (Number.isNaN(parsed)) {
    return parseFloat(fallback);
  }
  return parsed;
}

/**
 * Build the object that will be passed to `Sentry.init`. Pure — no side
 * effects — for testability. Returns `null` if DSN is missing.
 *
 * Contract (card kqb7X5iN, batch 43 F0.3):
 * - `tracesSampleRate`  ← VITE_SENTRY_TRACES_SAMPLE_RATE, default 0.2
 * - `replaysSessionSampleRate`  ← VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, default 0.05
 * - `replaysOnErrorSampleRate`  = 1.0 (constant)
 * - Replay integration always with `maskAllText: true` (PRIVACY: regla dura,
 *   nunca `false`).
 */
export function buildSentryConfig(): SentryConfig | null {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return null;
  }

  // Card u7eQ6itN — el nombre del entorno y el release salen de
  // `analytics/buildEnv.ts` y de ningún otro lado: son los mismos resolvers
  // que llenan `contexts.product_event.*`, para que el campo nativo y el del
  // context no puedan divergir. Al centralizarlo (u7eQ6itN) se conservó la
  // precedencia que vivía acá; desde jsCaNZfn el release además cae al SHA del
  // commit que inyecta Vercel, porque la variable que se leía no la define
  // ningún deploy. **No volver a leer las env vars en este archivo** — hay un
  // guard estructural en `analytics/__tests__/buildEnv.test.ts`.
  const environment = resolveEnvironment();
  const release = resolveRelease();

  const tracesSampleRate = readSampleRate('VITE_SENTRY_TRACES_SAMPLE_RATE', '0.2');
  const replaysSessionSampleRate = readSampleRate(
    'VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
    '0.05',
  );
  const replaysOnErrorSampleRate = 1.0;

  // Privacy invariant: maskAllText MUST be true. Card kqb7X5iN — regla dura.
  // NEVER flip to false without a security review.
  const replayOptions = {
    maskAllText: true,
    blockAllMedia: true,
    maskAttributes: [...REPLAY_MASKED_ATTRIBUTES],
  };

  return {
    dsn,
    environment,
    release,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    sendDefaultPii: false,
    integrations: [],
    tracing: Sentry.browserTracingIntegration(),
    tracesSampler: ({ parentSampled }) => {
      if (!hasAnalyticsConsent()) return 0;
      if (typeof parentSampled === 'boolean') return parentSampled;
      return tracesSampleRate;
    },
    replay: Sentry.replayIntegration({
      ...replayOptions,
      // Card CW31sjZO — los eventos custom de Replay (spans de red,
      // navegación, breadcrumbs de consola y de clicks) llevan las mismas
      // URLs y los atributos del elemento clickeado.
      beforeAddRecordingEvent: (event) => scrubDeep(event),
    }),
    replayOptions,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubErrorEvent,
    beforeSendTransaction: scrubTransactionEvent,
    beforeSendSpan: scrubSpan,
  };
}

/**
 * Sólo para tests: permite inyectar el `transport` para leer el envelope que
 * el SDK realmente arma, sin tocar la red. En producción se llama sin args.
 */
export interface InitSentryOptions {
  transport?: Sentry.BrowserOptions['transport'];
}

export function initSentry(options: InitSentryOptions = {}): boolean {
  if (initialized) return true;

  const config = buildSentryConfig();
  if (!config) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] VITE_SENTRY_DSN not set — error tracking DISABLED on frontend');
    return false;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    sendDefaultPii: config.sendDefaultPii,
    // Card mggUrIZI: `tracesSampler` decide (y lee el consentimiento) en vez
    // de `tracesSampleRate`.
    tracesSampler: config.tracesSampler,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    integrations: config.integrations as any,
    beforeBreadcrumb: config.beforeBreadcrumb,
    beforeSend: config.beforeSend,
    beforeSendTransaction: config.beforeSendTransaction,
    beforeSendSpan: config.beforeSendSpan,
    ...(options.transport ? { transport: options.transport } : {}),
  });

  Sentry.setTag('app', 'inbest-hub-web');
  Sentry.addEventProcessor(scrubReplayEvent);
  syncOptionalTelemetry(config);
  consentListener = () => syncOptionalTelemetry(config);
  window.addEventListener(CONSENT_CHANGED_EVENT, consentListener);

  initialized = true;
  // eslint-disable-next-line no-console
  console.info(
    `[sentry] initialized (env=${config.environment} release=${(config.release || 'unknown').slice(0, 8)} errors=on analytics=${hasAnalyticsConsent() ? 'granted' : 'denied'} tracesSampleRate=${config.tracesSampleRate} replaysSessionSampleRate=${config.replaysSessionSampleRate})`,
  );
  return true;
}

/**
 * Prende o apaga lo que pide consentimiento de analíticas (Performance y
 * Replay). Card mggUrIZI. Idempotente: se llama en el init y en cada
 * `consent-changed`.
 */
function syncOptionalTelemetry(config: SentryConfig): void {
  if (hasAnalyticsConsent()) {
    if (!tracingAdded) {
      tracingAdded = true;
      try {
        Sentry.addIntegration(config.tracing);
      } catch {
        // Telemetría: nunca rompe la app.
      }
    }
    if (!replayRequested) {
      replayRequested = true;
      startReplayWhenUrlIsSafe(config.replay);
    }
    return;
  }

  // Sin consentimiento (o revocado): Replay no arranca, y si ya grababa se
  // detiene. Las trazas las corta `tracesSampler` / `beforeSendTransaction`.
  if (deferredReplayTimer !== null) {
    clearInterval(deferredReplayTimer);
    deferredReplayTimer = null;
    replayRequested = false;
  }
  if (Sentry.getClient()?.getIntegrationByName('Replay')) {
    try {
      void config.replay.stop();
    } catch {
      // Telemetría: nunca rompe la app.
    }
  }
}

/**
 * Test-only: reset the "already initialized" latch so buildSentryConfig()
 * and initSentry() can be re-exercised with different envs. Intentionally
 * not exported from a barrel — call sites are limited to unit tests.
 */
export function __resetSentryForTesting(): void {
  initialized = false;
  tracingAdded = false;
  replayRequested = false;
  if (consentListener !== null) {
    window.removeEventListener(CONSENT_CHANGED_EVENT, consentListener);
    consentListener = null;
  }
  if (deferredReplayTimer !== null) {
    clearInterval(deferredReplayTimer);
    deferredReplayTimer = null;
  }
}

/**
 * Agrega Replay apenas la URL de la página no lleva params sensibles. Card
 * CW31sjZO (iteración #3).
 *
 * Replay graba `window.location.href` en el evento `Meta` de rrweb y en
 * `replay_event.urls` desde que arranca, y el primero no pasa por ningún hook
 * del SDK (`beforeAddRecordingEvent` sólo ve eventos custom). Medido por el
 * QA: el `code`/`state` del callback de Auth0 llegaban en claro. La única
 * forma de que no se graben es no estar grabando mientras están en la URL.
 *
 * En el caso normal (URL limpia) se agrega en el acto, con el mismo muestreo
 * de `Sentry.init`. En el callback, Auth0 limpia la URL (`onRedirectCallback`
 * navega a `returnTo`) y Replay arranca en el siguiente chequeo. Costo
 * aceptado: un error que ocurra ANTES de eso no trae replay.
 */
function startReplayWhenUrlIsSafe(replay: SentryConfig['replay']): void {
  const addReplay = (): void => {
    try {
      Sentry.addIntegration(replay);
    } catch {
      // Telemetría: nunca rompe la app.
    }
  };
  if (!hasSensitiveUrlParams(window.location.href)) {
    addReplay();
    return;
  }
  deferredReplayTimer = setInterval(() => {
    if (hasSensitiveUrlParams(window.location.href)) return;
    if (deferredReplayTimer !== null) clearInterval(deferredReplayTimer);
    deferredReplayTimer = null;
    addReplay();
  }, REPLAY_URL_POLL_MS);
}

export { Sentry };
